import { forwardRef } from 'react';

import { intToOneDigitStr, unixTimeToAgo } from '@shared/utils';

import { Contact } from '../-types';
import { ContactAvatar } from './contact-avatar';
import { trpc } from '@web/lib/trpc';
import { Badge } from '@web/components/ui/badge';
import { sepiaShade } from '@web/utils';
import { useAuthContext } from '@web/auth';

import { salesPipelineContactStatusDisplayName } from '@shared/pipelines/sales-pipeline/utils';

import { cn } from '@web/lib/shadcn';

const maxAge = 1000 * 60 * 60 * 24 * 60; // 60 days

export const ContactCard = forwardRef<
  HTMLDivElement,
  {
    contact: Contact;
    expanded?: boolean;
    active?: boolean;
    onSelect?: (contactId: string) => void;
    isMobileView?: boolean;
  }
>(({ contact, expanded = true, active = false, onSelect, isMobileView = false }, ref) => {
  const { user } = useAuthContext();
  const userId = user?.id ?? '';

  const { data: messagesData } = trpc.messaging.getMessages.useInfiniteQuery(
    { contactId: contact.id, userId },

    {
      staleTime: Infinity,

      getNextPageParam: (lastPage) => {
        const lastMessage = lastPage.at(-1);
        if (!lastMessage) return null; // last page empty

        return {
          dateTo: lastMessage.timestamp,
        };
      },
    },
  );

  const messages = messagesData?.pages.flat().toReversed() ?? [];
  const numUnread = messages.filter((message) => !message.isReadByUser).length;

  const lastMessage = messages.at(-1);
  const lastMessageDate = lastMessage?.timestamp;
  const lastMessageAge = lastMessageDate ? Date.now() - lastMessageDate : 0;
  const sepiaLevel = lastMessageAge / maxAge;

  // Get the last message content preview (similar to WhatsApp)
  const lastMessageContent = lastMessage?.content || '';
  const truncatedMessageContent = lastMessageContent.length > 40 
    ? lastMessageContent.substring(0, 40) + '...' 
    : lastMessageContent;

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex cursor-pointer items-center px-3 py-3 border-b border-gray-100 transition-colors',
        // WhatsApp style hover and active states
        'hover:bg-gray-50',
        active && 'bg-blue-50',
      )}
      onClick={() => {
        onSelect?.(contact.id);
      }}
      data-testid="contact-card"
    >
      {/* Avatar with online indicator */}
      <div className="relative flex-shrink-0">
        <ContactAvatar 
          name={contact.name} 
          className={cn(
            "border border-gray-200", 
            isMobileView ? "h-12 w-12" : "h-11 w-11"
          )} 
        />
        
        {/* Online indicator dot - only show for active conversation status */}
        {contact.status === 'active_conversation' && (
          <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-white"></span>
        )}
      </div>

      {expanded && (
        <div className="ml-3 flex-1 min-w-0 overflow-hidden pr-1">
          {/* Top row: Contact name and time */}
          <div className="flex items-center justify-between w-full">
            <p className="truncate font-medium text-gray-900 max-w-[70%]">
              {contact.name}
            </p>

            <div className="flex-shrink-0 flex items-center gap-1">
              {/* Last message time in WhatsApp style */}
              {lastMessageDate && (
                <span className="text-xs text-gray-500 min-w-[40px] text-right">
                  {unixTimeToAgo(lastMessageDate)}
                </span>
              )}
            </div>
          </div>

          {/* Bottom row: Message preview and badges */}
          <div className="flex items-center justify-between mt-0.5 w-full">
            {/* Last message preview with truncation */}
            <div className="flex items-center gap-1 max-w-[60%] min-w-0">
              {/* Check mark for outbound messages (like WhatsApp) */}
              {lastMessage?.direction === 'outbound' && (
                <svg className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              
              {/* Message preview */}
              <p className="truncate text-xs text-gray-500">
                {truncatedMessageContent || "No messages yet"}
              </p>
            </div>

            <div className="flex-shrink-0 flex items-center gap-1 ml-1">
              {/* Status Badge */}
              {contact.status && (
                <Badge 
                  variant="outline" 
                  className="text-[10px] px-1.5 py-0 rounded-md h-4 text-gray-500 bg-gray-100 border-gray-200 whitespace-nowrap"
                >
                  {salesPipelineContactStatusDisplayName({
                    status: contact.status,
                  })}
                </Badge>
              )}
              
              {/* Unread count badge in WhatsApp style */}
              {numUnread > 0 && (
                <Badge className="size-5 justify-center p-0 text-xs rounded-full bg-green-500 text-white border-0 flex-shrink-0 ml-1">
                  {intToOneDigitStr(numUnread)}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ContactCard.displayName = 'ContactCard';
